import { useCallback, useEffect, useRef, useState } from 'react';
import { inactiveRowCount } from '../../components/views/Parameters/parametersTableUtils';
import { salesChannelsService } from '../../services/commercialService';
import { notify } from '../../assets/helpers/toast';
import { createSalesChannelSchema } from '../../assets/schemas/schemas';
import { yupResolver } from '@hookform/resolvers/yup';
import { initialPager } from '../../components/views/Parameters/parametersConstants';
import { useForm } from 'react-hook-form';

export default function useSalesChannels() {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(createSalesChannelSchema()),
    defaultValues: {
      channel: '',
      shortName: '',
      isActive: true,
    },
  });
  const [showSalesChannelForm, setShowSalesChannelForm] = useState(false);
  const [commercialSubTab, setCommercialSubTab] = useState('channels');
  const [searchInputValue, setSearchInputValue] = useState('');
  const [search, setSearch] = useState('');
  const searchDebouncedRef = useRef('');
  const [filterChannelIds, setFilterChannelIds] = useState([]);

  const [channelOptions, setChannelOptions] = useState([]);
  const [channelOptionsLoading, setChannelOptionsLoading] = useState(false);

  const [salesChannelData, setSalesChannelData] = useState({
    limit: initialPager.limit,
    offset: 0,
    data: [],
    count: 0,
    isLoading: false,
  });

  const [editingSalesChannelId, setEditingSalesChannelId] = useState(null);

  const handleSalesChannelsPageChange = useCallback((_e, offset) => {
    setSalesChannelData((prev) => ({
      ...prev,
      offset: (offset - 1) * prev.limit,
    }));
  }, []);

  const handleToggleSalesChannelActive = useCallback(async (row) => {
    const next = !row.isActive;
    const response = await salesChannelsService.updateSalesChannel(row._id, {
      isActive: next,
    });

    if ([200, 201].includes(response?.status)) {
      notify(next ? 'Activo' : 'Desactivado', 'success');
      setSalesChannelData((prev) => ({
        ...prev,
        data: prev.data.map((item) =>
          item._id === row._id ? { ...item, isActive: next } : item
        ),
      }));
    } else {
      notify(
        response?.message ??
        response?.data?.message ??
        'Error al actualizar el estado del modelo',
        'error'
      );
    }
  }, []);

  const handleEditSalesChannel = useCallback(
    (row) => {
      setShowSalesChannelForm(true);
      setEditingSalesChannelId(row._id);
      reset({
        channel: row.name,
        shortName: row.shortName,
        isActive: row.isActive,
      });
    },
    [reset]
  );

  const onSalesChannelSelectPerPage = (event) => {
    const value = Number(event.target.value);
    setSalesChannelData((prev) => ({
      ...prev,
      offset: 0,
      limit: value,
    }));
  };

  const handleChannelIdsFilter = useCallback((channelsIds) => {
    setFilterChannelIds(channelsIds);
    setSalesChannelData((prev) => ({ ...prev, offset: 0 }));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setChannelOptionsLoading(true);
    salesChannelsService
      .getSalesChannelsByIds({ offset: 0, limit: 100000, channelsIds: [] })
      .then((response) => {
        if (cancelled) return;
        if ([200, 201].includes(response?.status)) {
          setChannelOptions(response?.data?.saleChannels ?? []);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setChannelOptionsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      const trimmed = searchInputValue.trim();
      if (trimmed === searchDebouncedRef.current) return;
      searchDebouncedRef.current = trimmed;
      setSearch(trimmed);
      setSalesChannelData((prev) => ({ ...prev, offset: 0 }));
    }, 500);
    return () => clearTimeout(t);
  }, [searchInputValue]);

  const fetchSalesChannels = useCallback(
    async (abortSignal) => {
      setSalesChannelData((prev) => ({ ...prev, isLoading: true }));

      try {
        const response = await salesChannelsService.getSalesChannelsByIds({
          offset: salesChannelData.offset,
          limit: salesChannelData.limit,
          channelsIds: filterChannelIds,
          ...(search ? { description: search } : {}),
        });

        if (abortSignal?.aborted) return;

        if ([200, 201].includes(response?.status)) {
          const rows =
            response?.data?.saleChannels?.map((channel) => ({
              ...channel,
              links: [
                { label: 'F2', link: channel.f2Link },
                { label: 'F4', link: channel.f4Link },
                { label: 'Retail', link: channel.retailLink },
              ],
              qrCodes: [
                { label: 'QR F2', link: channel.qrCodeF2 },
                { label: 'QR F4', link: channel.qrCodePlusF4 },
                { label: 'QR +', link: channel.qrCode },
              ],
            })) || [];

          setSalesChannelData((prev) => ({
            ...prev,
            data: rows,
            count: response?.data?.total ?? rows.length,
            isLoading: false,
          }));
        } else {
          notify(
            response?.message ??
            response?.data?.message ??
            'Error al cargar la lista de canales de venta',
            'error'
          );
          setSalesChannelData((prev) => ({
            ...prev,
            isLoading: false,
          }));
        }
      } catch (error) {
        if (abortSignal?.aborted) return;
        notify('Error al cargar la lista de canales de venta', 'error');
        setSalesChannelData((prev) => ({ ...prev, isLoading: false }));
      }
    },
    [salesChannelData.offset, salesChannelData.limit, filterChannelIds, search]
  );

  const onSubmit = handleSubmit(async (data) => {
    if (editingSalesChannelId) {
      const response = await salesChannelsService.updateSalesChannel(
        editingSalesChannelId,
        { channel: data.channel, shortName: data.shortName }
      );

      if ([200, 201].includes(response?.status)) {
        notify('Canal de venta actualizado', 'success');
        fetchSalesChannels();
        setEditingSalesChannelId(null);
        setShowSalesChannelForm(false);
        reset({ channel: '', shortName: '', isActive: true });
      } else {
        notify(
          response?.message ??
          response?.data?.message ??
          'Error al actualizar el canal de venta',
          'error'
        );
      }
      return;
    }

    const response = await salesChannelsService.createSalesChannel({
      channel: data.channel,
      shortName: data.shortName,
    });

    if ([200, 201].includes(response?.status)) {
      notify('Canal de venta creado', 'success');
      reset({ channel: '', shortName: '', isActive: true });
      setShowSalesChannelForm(false);
      fetchSalesChannels();
    } else {
      notify(
        response?.message ??
        response?.data?.message ??
        'Error al crear el canal de venta',
        'error'
      );
    }
  });

  const closeForm = () => {
    setShowSalesChannelForm(false);
    setEditingSalesChannelId(null);
    reset({ channel: '', shortName: '', isActive: true });
  }

  useEffect(() => {
    const abortController = new AbortController();
    fetchSalesChannels(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [fetchSalesChannels]);

  const inactiveSalesChannels = inactiveRowCount(salesChannelData.data);
  return {
    register,
    errors,
    showSalesChannelForm,
    commercialSubTab,
    setCommercialSubTab,
    handleSalesChannelsPageChange,
    handleToggleSalesChannelActive,
    handleEditSalesChannel,
    onSalesChannelSelectPerPage,
    onSubmit,
    inactiveSalesChannels,
    setShowSalesChannelForm,
    editingSalesChannelId,
    salesChannelData,
    closeForm,
    searchInputValue,
    setSearchInputValue,
    filterChannelIds,
    handleChannelIdsFilter,
    channelOptions,
    channelOptionsLoading,
  };
}
